import { useCallback, useInsertionEffect, useRef, useState } from 'react';

/**
 * A HANDLER THAT KEEPS ITS IDENTITY (2026-10-30): the same function every render, calling whichever `fn` the last
 * commit gave. For a handler handed to a memoised child that it only calls on an event: a fresh closure each render
 * made the child render with it - both body plates, ~150 parts, on every one of the seven renders after a drop,
 * for a right-click handler nobody pressed.
 *
 * Not for anything called DURING render: the latest `fn` is taken at commit (useInsertionEffect, before any layout
 * effect or event can run), so a render that calls it would see the previous one.
 */
/**
 * @template {(...args: any[]) => any} F
 * @param {F} fn
 * @returns {F}
 */
export default function useStableCallback(fn) {
  const latest = useRef(fn);
  useInsertionEffect(() => {
    latest.current = fn;
  });
  return useCallback((...args) => latest.current(...args), []);
}

/**
 * A SET OF HANDLERS THAT KEEPS ITS IDENTITY: the same object of the same functions every render, each calling the
 * latest handler of its name - for a row of memoised children all handed the same handlers (the order rail's rows).
 * The names are the first render's; every handler is called, never tested for presence. Same caveat as above.
 *
 * @template {Record<string, (...args: any[]) => any>} T
 * @param {T} handlers
 * @returns {T}
 */
export function useStableHandlers(handlers) {
  const latest = useRef(handlers);
  useInsertionEffect(() => {
    latest.current = handlers;
  });
  const [stable] = useState(() =>
    Object.fromEntries(Object.keys(handlers).map((name) => [name, (...args) => latest.current[name](...args)])),
  );
  return stable;
}

/**
 * A SCOPE'S HANDLERS, STABLE (2026-10-30): for each function in `scope` whose name `isHandler` accepts, one wrapper
 * kept for the life of the component, calling the latest function of that name. The result holds only those names,
 * for `Object.assign(scope, ...)`: a handler absent this render stays absent, so a test for it still says none.
 *
 * The shell's scope (DeathWatchSimulator) is the use: its handlers were closures made fresh each render, and every
 * context built from it took a new value with them - so every memoised panel reading a context rendered with each
 * of the table's renders (seven after a drop). Same caveat as above: never for a function called during render.
 *
 * @template {Record<string, unknown>} T
 * @param {T} scope
 * @param {(name: string) => boolean} isHandler
 * @returns {Partial<T>}
 */
export function useStableHandlersOf(scope, isHandler) {
  const raw = {};
  for (const [name, value] of Object.entries(scope)) {
    if (typeof value === 'function' && isHandler(name)) raw[name] = value;
  }
  const latest = useRef(raw);
  useInsertionEffect(() => {
    latest.current = raw;
  });
  const [wrappers] = useState(() => new Map());
  const stable = {};
  for (const name of Object.keys(raw)) {
    if (!wrappers.has(name)) wrappers.set(name, (...args) => latest.current[name]?.(...args));
    stable[name] = wrappers.get(name);
  }
  return stable;
}

const sameFields = (a, b) => {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((k) => Object.is(a[k], b[k]));
};

/**
 * AN API OBJECT THAT KEEPS ITS IDENTITY WHILE ITS DATA DOES (plan shell-contexts, A4): every function in `api`
 * becomes a stable wrapper calling the latest of its name, and the object returned is the last one until a data
 * field (anything not a function) changes identity. For a hook's answer of data plus ACTIONS - the chat, the
 * channels, the handouts - rebuilt as a new object on every render, which a context then held. Never for one with a
 * function read while drawing (the handouts' `seenOf`): wrapped, it would answer from the last commit.
 *
 * @template {Record<string, unknown>} T
 * @param {T} api
 * @returns {T}
 */
export function useStableApi(api) {
  const latest = useRef(api);
  useInsertionEffect(() => {
    latest.current = api;
  });
  const [wrappers] = useState(() => new Map());
  const kept = useRef(null);
  const next = withStableFunctions(api, wrappers, latest);
  if (!kept.current || !sameFields(kept.current, next)) kept.current = next;
  return kept.current;
}

/** `api` with each function swapped for its kept wrapper (made on first sight), calling `latest`'s of that name. */
function withStableFunctions(api, wrappers, latest) {
  const out = {};
  for (const [name, value] of Object.entries(api)) {
    if (typeof value === 'function' && !wrappers.has(name)) {
      wrappers.set(name, (...args) => latest.current[name]?.(...args));
    }
    out[name] = typeof value === 'function' ? wrappers.get(name) : value;
  }
  return out;
}
